/*
 * Timezone helpers. Timezones are stored as IANA zones only (eg America/Chicago).
 *
 * Date pickers and new Date(y, m, d) work in the browser timezone, but reports
 * need day/month boundaries in the company timezone, so these move wall clock
 * times between zones using Temporal.
 */
import { Temporal } from 'temporal-polyfill';
import ianaTimezones from 'src/assets/data/IANA-time-zone.json';

// Options for a timezone select, eg { title: 'US (Common) - Chicago (Central)', value: 'America/Chicago' }
export const timezoneItems = ianaTimezones.flatMap((group) =>
  group.zones.map((zone) => ({
    title: group.group + ' - ' + zone.name,
    value: zone.value,
  }))
);

export const isValidTimezone = (timeZone) => {
  if (!timeZone) {
    return false;
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch (e) {
    return false;
  }
};

export const browserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/*
 * Build the instant for a wall clock time in timeZone, like new Date(y, m, d...)
 * but in that zone instead of the browser's. Month is 0 based like Date.
 * On DST changes a skipped time moves forward and a repeated time uses the first.
 */
export const zonedDate = (
  timeZone,
  year,
  month,
  day = 1,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0
) => {
  // Let Date roll over values like day 0 (last day of the month before)
  const wall = new Date(
    Date.UTC(year, month, day, hour, minute, second, millisecond)
  );
  const zoned = Temporal.PlainDateTime.from({
    year: wall.getUTCFullYear(),
    month: wall.getUTCMonth() + 1,
    day: wall.getUTCDate(),
    hour: wall.getUTCHours(),
    minute: wall.getUTCMinutes(),
    second: wall.getUTCSeconds(),
    millisecond: wall.getUTCMilliseconds(),
  }).toZonedDateTime(timeZone, { disambiguation: 'compatible' });
  return new Date(zoned.epochMilliseconds);
};

/*
 * Keep the same wall clock time but move it to timeZone. Use for dates from
 * pickers: 'Mar 1 00:00' picked in the browser becomes 'Mar 1 00:00' company time.
 */
export const toZoned = (date, timeZone) => {
  if (date == undefined || date === '' || !timeZone) {
    return date;
  }
  const d = new Date(date);
  if (isNaN(d)) {
    return date;
  }
  return zonedDate(
    timeZone,
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds()
  );
};

/*
 * The calendar day a date picker value stands for, as 'YYYY-MM-DD'. Date only
 * reports send this so the API reads it as that day in the company timezone,
 * rather than an instant that can land on another day once converted.
 */
export const toDateOnly = (value) => {
  if (value == undefined || value === '') {
    return value;
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const d = new Date(value);
  if (isNaN(d)) {
    return value;
  }
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};
